import { useState, useEffect } from 'react'
import { 
  ExclamationTriangleIcon, 
  EyeIcon, 
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { StarIcon as SolidStarIcon } from '@heroicons/react/24/solid'
import UserAvatar from '../../components/UserAvatar'

interface Booking {
  id: string
  student: {
    firstName: string
    lastName: string
    email: string
    avatar?: string
  }
  course: {
    title: string
    subject: string
  }
  rating: number
  review?: string
  date: string
  startTime: string
  endTime: string
  totalPrice: number
}

interface Dispute {
  id: string
  student: {
    firstName: string
    lastName: string
    email: string
    avatar?: string
  }
  course: {
    title: string
    subject: string
  }
  rating: number
  review?: string
  disputeReason?: string
  disputeStatus?: 'pending' | 'approved' | 'rejected'
  disputeSubmittedAt?: string
  disputeReviewedAt?: string
  disputeReviewNotes?: string
  ratingExcluded: boolean
  date: string
  startTime: string
  endTime: string
}

const TeacherRatingDisputes = () => {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [disputes, setDisputes] = useState<Dispute[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [showDisputeModal, setShowDisputeModal] = useState(false)
  const [disputeReason, setDisputeReason] = useState('')
  const [activeTab, setActiveTab] = useState<'ratings' | 'disputes'>('ratings')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // 获取教师的预约记录（包含评分）
      const bookingsResponse = await fetch('/api/bookings/teacher', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      // 获取异议记录
      const disputesResponse = await fetch('/api/rating-disputes/my-disputes', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (bookingsResponse.ok) {
        const bookingsData = await bookingsResponse.json()
        // 只显示已完成且有评分的预约
        const ratedBookings = bookingsData.filter((booking: any) => 
          booking.status === 'completed' && booking.rating && booking.rating > 0
        )
        setBookings(ratedBookings)
      }

      if (disputesResponse.ok) {
        const disputesData = await disputesResponse.json()
        setDisputes(disputesData)
      }
    } catch (error) {
      console.error('加载数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitDispute = async () => {
    if (!selectedBooking || !disputeReason.trim()) {
      alert('请填写异议原因')
      return
    }

    try {
      const response = await fetch('/api/rating-disputes/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          bookingId: selectedBooking.id,
          reason: disputeReason
        })
      })

      if (response.ok) {
        alert('异议提交成功，等待管理员审核')
        setShowDisputeModal(false)
        setSelectedBooking(null)
        setDisputeReason('')
        loadData() // 重新加载数据
      } else {
        const error = await response.json()
        alert(`提交失败: ${error.message}`)
      }
    } catch (error) {
      console.error('提交异议失败:', error)
      alert('提交失败，请重试')
    }
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <div key={star} className="relative">
            {star <= rating ? (
              <SolidStarIcon className="h-5 w-5 text-yellow-400" />
            ) : (
              <StarIcon className="h-5 w-5 text-gray-300" />
            )}
          </div>
        ))}
        <span className="ml-2 text-sm text-gray-600">({rating}/5)</span>
      </div>
    )
  }

  const getDisputeStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'approved': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDisputeStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '待审核'
      case 'approved': return '已通过'
      case 'rejected': return '已拒绝'
      default: return '未知'
    }
  }

  const getDisputeStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ClockIcon className="h-4 w-4" />
      case 'approved': return <CheckCircleIcon className="h-4 w-4" />
      case 'rejected': return <XCircleIcon className="h-4 w-4" />
      default: return null
    }
  }

  const canDispute = (booking: Booking) => {
    // 只能对1-2星评分提出异议
    if (booking.rating > 2) return false
    
    // 检查是否已经有异议
    const existingDispute = disputes.find(d => d.id === booking.id)
    return !existingDispute
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面标题 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">评分管理</h1>
        <p className="mt-2 text-sm text-gray-700">查看学生评价，对不当评分提出异议</p>
      </div>

      {/* 选项卡 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('ratings')}
            className={`${
              activeTab === 'ratings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            学生评价
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
              {bookings.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('disputes')}
            className={`${
              activeTab === 'disputes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
          >
            异议记录
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
              {disputes.length}
            </span>
          </button>
        </nav>
      </div>

      {/* 学生评价列表 */}
      {activeTab === 'ratings' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {bookings.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {bookings.map((booking) => (
                <li key={booking.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <UserAvatar user={booking.student} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {booking.student.firstName} {booking.student.lastName}
                            </p>
                            <div className="flex items-center">
                              {renderStars(booking.rating)}
                            </div>
                            {booking.rating <= 2 && canDispute(booking) && (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                可异议
                              </span>
                            )}
                          </div>
                          <div className="mt-1">
                            <p className="text-sm text-gray-900 font-medium">{booking.course.title}</p>
                            <p className="text-sm text-gray-500">
                              {booking.course.subject} • {new Date(booking.date).toLocaleDateString('zh-CN')} {booking.startTime}-{booking.endTime}
                            </p>
                            {booking.review && (
                              <p className="text-sm text-gray-600 mt-2 italic">"{booking.review}"</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {booking.rating <= 2 && canDispute(booking) && (
                          <button
                            onClick={() => {
                              setSelectedBooking(booking)
                              setShowDisputeModal(true)
                            }}
                            className="inline-flex items-center px-3 py-1 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                            提出异议
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-12">
              <StarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">暂无评价记录</h3>
              <p className="mt-1 text-sm text-gray-500">完成课程后学生会给出评价</p>
            </div>
          )}
        </div>
      )}

      {/* 异议记录列表 */}
      {activeTab === 'disputes' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {disputes.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {disputes.map((dispute) => (
                <li key={dispute.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <UserAvatar user={dispute.student} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {dispute.student.firstName} {dispute.student.lastName}
                            </p>
                            <div className="flex items-center">
                              {renderStars(dispute.rating)}
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDisputeStatusColor(dispute.disputeStatus || '')}`}>
                              {getDisputeStatusIcon(dispute.disputeStatus || '')}
                              <span className="ml-1">{getDisputeStatusLabel(dispute.disputeStatus || '')}</span>
                            </span>
                            {dispute.ratingExcluded && (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                已排除计算
                              </span>
                            )}
                          </div>
                          <div className="mt-1">
                            <p className="text-sm text-gray-900 font-medium">{dispute.course.title}</p>
                            <p className="text-sm text-gray-500">
                              异议提交时间: {new Date(dispute.disputeSubmittedAt || '').toLocaleString('zh-CN')}
                            </p>
                            <div className="mt-2">
                              <p className="text-sm text-gray-600"><strong>原评价:</strong> "{dispute.review}"</p>
                              <p className="text-sm text-gray-600 mt-1"><strong>异议原因:</strong> {dispute.disputeReason}</p>
                              {dispute.disputeReviewNotes && (
                                <p className="text-sm text-gray-600 mt-1"><strong>审核意见:</strong> {dispute.disputeReviewNotes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">暂无异议记录</h3>
              <p className="mt-1 text-sm text-gray-500">您还没有提交过评分异议</p>
            </div>
          )}
        </div>
      )}

      {/* 提交异议模态框 */}
      {showDisputeModal && selectedBooking && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">提出评分异议</h3>
              <button
                onClick={() => setShowDisputeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">关闭</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <p className="text-sm text-gray-600">
                  <strong>课程:</strong> {selectedBooking.course.title}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>学生:</strong> {selectedBooking.student.firstName} {selectedBooking.student.lastName}
                </p>
                <div className="flex items-center mt-1">
                  <strong className="text-sm text-gray-600 mr-2">评分:</strong>
                  {renderStars(selectedBooking.rating)}
                </div>
                {selectedBooking.review && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>评价:</strong> "{selectedBooking.review}"
                  </p>
                )}
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                异议原因 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请详细说明您认为该评分不公正的原因..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                请提供详细的事实和理由，管理员将据此审核您的异议
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDisputeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                取消
              </button>
              <button
                onClick={handleSubmitDispute}
                disabled={!disputeReason.trim()}
                className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                提交异议
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeacherRatingDisputes 